import { useMemo } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { Trackpoint } from '../api/types'
import { pace } from '../format'

interface Sample {
  km: number
  bpm?: number
  paceSecondsPerKm?: number
  altitude?: number
}

/**
 * Heart rate, pace and elevation against distance.
 *
 * Distance on the x-axis rather than time, so a stop at a traffic light does not stretch the trace
 * and splits line up with the kilometre markers a runner remembers.
 */
export function TrackCharts({ points }: { points: Trackpoint[] }) {
  const samples = useMemo(() => toSamples(points), [points])

  const hasHeartRate = samples.some((s) => s.bpm !== undefined)
  const hasPace = samples.some((s) => s.paceSecondsPerKm !== undefined)
  const hasAltitude = samples.some((s) => s.altitude !== undefined)

  // Axes are scaled to the data, not anchored at zero. A heart rate that never leaves 120-175 is
  // a flat line in the top third of a 0-180 axis; the interesting variation is the whole point of
  // the chart, so the range has to be the runner's range.
  const hrDomain = useMemo(() => paddedDomain(samples.map((s) => s.bpm), 5, 0), [samples])
  const paceDomain = useMemo(
    () => paddedDomain(samples.map((s) => s.paceSecondsPerKm), 15, 0),
    [samples],
  )
  const altitudeDomain = useMemo(() => paddedDomain(samples.map((s) => s.altitude), 5), [samples])

  if (samples.length < 2) {
    return null
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {hasHeartRate && (
        <div className="chart-card">
          <div className="chart-title">Heart rate (bpm)</div>
          <ResponsiveContainer width="100%" height={170}>
            <LineChart data={samples} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="km"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(v: number) => v.toFixed(1)}
                stroke="var(--text-dim)"
                fontSize={12}
                tickLine={false}
              />
              <YAxis
                domain={hrDomain}
                allowDecimals={false}
                stroke="var(--text-dim)"
                fontSize={12}
                tickLine={false}
                width={44}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelFormatter={(v) => `${Number(v).toFixed(2)} km`}
                formatter={(v) => [`${v} bpm`, 'Heart rate']}
              />
              <Line
                type="monotone"
                dataKey="bpm"
                stroke="var(--heart)"
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {hasPace && (
        <div className="chart-card">
          <div className="chart-title">Pace (min/km)</div>
          <ResponsiveContainer width="100%" height={170}>
            <LineChart data={samples} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="km"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(v: number) => v.toFixed(1)}
                stroke="var(--text-dim)"
                fontSize={12}
                tickLine={false}
              />
              {/* Reversed: a faster pace is a smaller number, and should sit higher. */}
              <YAxis
                reversed
                domain={paceDomain}
                stroke="var(--text-dim)"
                fontSize={12}
                tickLine={false}
                width={44}
                tickFormatter={(v: number) => pace(v)}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelFormatter={(v) => `${Number(v).toFixed(2)} km`}
                formatter={(v) => [`${pace(Number(v))} /km`, 'Pace']}
              />
              <Line
                type="monotone"
                dataKey="paceSecondsPerKm"
                stroke="var(--accent)"
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {hasAltitude && (
        <div className="chart-card">
          <div className="chart-title">Elevation (m)</div>
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={samples} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="km"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(v: number) => v.toFixed(1)}
                stroke="var(--text-dim)"
                fontSize={12}
                tickLine={false}
              />
              <YAxis
                domain={altitudeDomain}
                allowDecimals={false}
                stroke="var(--text-dim)"
                fontSize={12}
                tickLine={false}
                width={44}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelFormatter={(v) => `${Number(v).toFixed(2)} km`}
                formatter={(v) => [`${Math.round(Number(v))} m`, 'Elevation']}
              />
              <Area
                type="monotone"
                dataKey="altitude"
                stroke="var(--text-dim)"
                fill="var(--surface-2)"
                strokeWidth={1.5}
                connectNulls
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

/**
 * A Y-axis range covering the data with a little breathing room, rounded to whole units so the
 * tick labels stay tidy. Returns Recharts' automatic domain when there is nothing to measure.
 *
 * @param pad how much headroom to leave on each side, in the series' own units.
 * @param floor clamps the lower bound, for quantities that cannot sensibly go below it.
 */
function paddedDomain(
  values: Array<number | undefined>,
  pad: number,
  floor?: number,
): [number, number] | undefined {
  const present = values.filter((v): v is number => v !== undefined && Number.isFinite(v))
  if (present.length === 0) return undefined
  let low = Math.floor(Math.min(...present) - pad)
  const high = Math.ceil(Math.max(...present) + pad)
  if (floor !== undefined) low = Math.max(floor, low)
  // A dead-flat series would otherwise collapse to a zero-height axis.
  return low === high ? [low - 1, high + 1] : [low, high]
}

const tooltipStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  color: 'var(--text)',
  fontSize: 13,
}

/**
 * Builds the chart series.
 *
 * Pace is smoothed over a rolling window instead of being taken from a single trackpoint: at
 * one-second sampling, per-point pace is dominated by GPS noise and the chart becomes unreadable.
 */
function toSamples(points: Trackpoint[]): Sample[] {
  const window = 15
  const samples: Sample[] = []

  for (let i = 0; i < points.length; i++) {
    const point = points[i]
    if (!point) continue
    const distance = point.distanceMeters ?? 0

    let paceSecondsPerKm: number | undefined
    const earlier = points[Math.max(0, i - window)]
    if (earlier && earlier !== point) {
      const metres = distance - (earlier.distanceMeters ?? 0)
      const seconds = (Date.parse(point.time) - Date.parse(earlier.time)) / 1000
      if (metres > 5 && seconds > 0) {
        const value = seconds / (metres / 1000)
        // Anything slower than 30 min/km is a stop, not a pace worth plotting.
        if (value < 1800) paceSecondsPerKm = value
      }
    }

    samples.push({
      km: distance / 1000,
      bpm: point.heartRateBpm,
      paceSecondsPerKm,
      altitude: point.altitudeMeters,
    })
  }

  // A long run can hold thousands of points; thinning keeps the SVG light without visible loss.
  const maxPoints = 600
  if (samples.length <= maxPoints) return samples
  const step = Math.ceil(samples.length / maxPoints)
  return samples.filter((_, index) => index % step === 0 || index === samples.length - 1)
}
